import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { clearAchievementId } from '@/config/achievements';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import { SECTORS, sectorAt } from '@/config/sectors';
import { WEAPONS } from '@/config/weapons';
import { weaponGlyphKey } from '@/config/weapon-visuals';
import { bestTime, isSectorCleared, isSectorUnlocked, sectorMedals } from '@/core/meta';
import { formatStat } from '@/core/records';
import { t, type I18nKey } from '@/i18n';
import { bossById } from '@/bosses/registry';
import { achievementDef, buildBadge } from './achievement-badges';
import { bodyText, chamferPanel, hexPath, label, targetBrackets } from './widgets';

/** Sector select design space (scaled to fit the canvas). */
export const SECTOR_LAYOUT = {
  w: 1100, h: 640,
  card: { w: 248, h: 372, gap: 36, top: 70 },
  loadoutY: 492,
  backY: 560,
  hintY: 628,
};
const C = SECTOR_LAYOUT.card;
const BAND_H = 34;
const DISC = { y: 142, r: 58 };
const SILHOUETTE = 0x0a0814;
const LOCKED_FILL = 0x100d18;
/** Feats shown as medals on each card (the sector clear first) */
const MEDALS = ['blitz', 'deathless', 'untouchable'] as const;

/** Left x of sector card `i` in design space. */
export function sectorCardX(i: number): number {
  const total = SECTORS.length * C.w + (SECTORS.length - 1) * C.gap;
  return (SECTOR_LAYOUT.w - total) / 2 + i * (C.w + C.gap);
}

/**
 * One sector card: band + name, the finale boss (silhouette until the
 * sector is cleared), flavor line, room count, medals, lock / best time.
 */
export function buildSectorCard(index: number, selected: boolean): Container {
  const sector = sectorAt(index);
  const open = isSectorUnlocked(index);
  const cleared = isSectorCleared(index);
  const accent = open ? sector.accent : 0x3a3450;
  const c = new Container();
  const bg = new Graphics();
  chamferPanel(bg, C.w, C.h, {
    fill: open ? (selected ? 0x231d36 : 0x161222) : LOCKED_FILL,
    stroke: selected ? COLORS.uiAccent : accent,
    strokeWidth: selected ? 4 : 3,
    notch: 16,
  });
  bg.poly([18, 6, C.w - 7, 6, C.w - 7, BAND_H, 7, BAND_H, 7, 18])
    .fill({ color: accent, alpha: open ? 0.9 : 0.5 });
  if (selected) targetBrackets(bg, C.w, C.h);
  c.addChild(bg);

  const tag = label(t('sector.label', { n: index + 1 }), 13, 0x0f0c1a, 3);
  tag.anchor.set(0.5);
  tag.position.set(C.w / 2, BAND_H / 2 + 3);
  const name = label(t(sector.nameKey as I18nKey), 19, open ? 0xffffff : 0x6a6480, 2);
  name.anchor.set(0.5, 0);
  name.position.set(C.w / 2, BAND_H + 12);
  if (name.width > C.w - 24) name.scale.set((C.w - 24) / name.width);
  c.addChild(tag, name);

  c.addChild(finaleDisc(sector.finale, accent, open, cleared));
  const finaleName = cleared ? ` · ${t(bossById(sector.finale)!.def.nameKey as I18nKey)}` : '';
  const finale = label(`${t('sector.finale')}${finaleName}`, 11, open ? accent : 0x4a4458, 2);
  finale.anchor.set(0.5, 0);
  finale.position.set(C.w / 2, DISC.y + DISC.r + 10);
  if (finale.width > C.w - 20) finale.scale.set((C.w - 20) / finale.width);
  c.addChild(finale);

  const flavor = bodyText(t(sector.tagKey as I18nKey), 12, open ? 0xb8bcc6 : 0x4a4458, C.w - 30);
  flavor.anchor.set(0.5, 0);
  flavor.position.set(C.w / 2, 236);
  const rooms = label(t('sector.rooms', { n: sector.rooms }), 11, 0x8a8f98, 2);
  rooms.anchor.set(0.5, 0);
  rooms.position.set(C.w / 2, 278);
  c.addChild(flavor, rooms);

  c.addChild(medalRow(index));
  c.addChild(statusLine(index, open, cleared));
  return c;
}

/** Finale boss on a glowing disc; locked sectors get a padlock over it. */
function finaleDisc(bossId: string, accent: number, open: boolean, cleared: boolean): Container {
  const c = new Container();
  const g = new Graphics();
  g.circle(C.w / 2, DISC.y, DISC.r).fill({ color: accent, alpha: open ? 0.22 : 0.08 })
    .stroke({ color: accent, width: 2, alpha: open ? 0.7 : 0.35 });
  c.addChild(g);
  const def = bossById(bossId)?.def;
  if (def) {
    const sprite = new Sprite(getTexture(def.bodyKey));
    sprite.anchor.set(0.5);
    sprite.scale.set((DISC.r * 1.4) / Math.max(sprite.texture.width, sprite.texture.height));
    sprite.position.set(C.w / 2, DISC.y);
    // The finale stays a silhouette until the sector is beaten
    if (!cleared) sprite.tint = SILHOUETTE;
    if (!open) sprite.alpha = 0.45;
    c.addChild(sprite);
  }
  if (!open) c.addChild(padlock(C.w / 2, DISC.y));
  return c;
}

/** Drawn padlock (no asset): shackle arc + body + keyhole. */
function padlock(cx: number, cy: number): Graphics {
  const g = new Graphics();
  g.arc(cx, cy - 6, 13, Math.PI, 0).stroke({ color: 0xc9ced6, width: 5 });
  g.roundRect(cx - 19, cy - 6, 38, 30, 5).fill(0xc9ced6).stroke({ color: COLORS.outline, width: 2 });
  g.circle(cx, cy + 5, 4).fill(COLORS.outline);
  g.rect(cx - 1.5, cy + 6, 3, 9).fill(COLORS.outline);
  return g;
}

function medalRow(index: number): Container {
  const c = new Container();
  const met = sectorMedals(index);
  const ids = [clearAchievementId(index), ...MEDALS];
  const step = 34;
  const x0 = C.w / 2 - ((ids.length - 1) * step) / 2;
  ids.forEach((id, i) => {
    const def = achievementDef(id);
    if (!def) return;
    const badge = buildBadge(def, met.includes(id), 12);
    badge.position.set(x0 + i * step, 312);
    c.addChild(badge);
  });
  return c;
}

function statusLine(index: number, open: boolean, cleared: boolean): Container {
  const best = bestTime(index);
  let text: string;
  let color: number;
  if (!open) {
    text = t('sector.lockedHint', { n: index });
    color = 0x8a8f98;
  } else if (cleared) {
    text = best !== null
      ? `${t('sector.cleared')} · ${t('sector.best', { time: formatStat('timeAlive', best) })}`
      : t('sector.cleared');
    color = COLORS.gemYellow;
  } else {
    text = t('sector.notCleared');
    color = 0x9aa0a6;
  }
  const status = label(text, 12, color, 1);
  status.anchor.set(0.5, 0);
  status.position.set(C.w / 2, 340);
  if (status.width > C.w - 20) status.scale.set((C.w - 20) / status.width);
  return status;
}

/** "START WEAPON ◀ NAME ▶" row, centred on (cx, cy) in design space. */
export function buildLoadoutRow(
  weaponId: string, choice: number, choices: number, selected: boolean, cx: number, cy: number,
): Container {
  const w = 560;
  const h = 54;
  const c = new Container();
  const bg = new Graphics();
  chamferPanel(bg, w, h, {
    fill: selected ? 0x2a2440 : 0x161222,
    stroke: selected ? COLORS.uiAccent : 0x4a4256,
    strokeWidth: selected ? 4 : 3,
    notch: 13,
  });
  if (selected) targetBrackets(bg, w, h);
  bg.position.set(-w / 2, -h / 2);
  c.addChild(bg);
  const head = label(t('sector.startWeapon'), 12, COLORS.uiAccent, 2);
  head.anchor.set(0, 0.5);
  head.position.set(-w / 2 + 20, 0);
  const def = WEAPONS[weaponId];
  const color = def ? WEAPON_CATEGORY_COLOR[def.category] : 0xffffff;
  const many = choices > 1;
  const name = label(
    `${many ? '◀  ' : ''}${def ? t(def.nameKey as I18nKey) : weaponId}${many ? '  ▶' : ''}`,
    19, selected ? COLORS.playerYellow : 0xffffff, 2,
  );
  name.anchor.set(0.5);
  name.position.set(50, 0);
  // The weapon's own glyph in a category-coloured hex — the same identity
  // as the crate, the bag, the sockets and the HUD (docs/09 wave 4)
  const gx = 50 - name.width / 2 - 26;
  const hex = hexPath(new Graphics(), gx, 0, 22).fill(0x0f0c1a).stroke({ color, width: 3 });
  const glyph = new Sprite(getTexture(weaponGlyphKey(weaponId)));
  glyph.anchor.set(0.5);
  glyph.scale.set(0.5);
  glyph.position.set(gx, 0);
  const count = label(`${choice + 1}/${choices}`, 12, 0x8a8f98, 1);
  count.anchor.set(1, 0.5);
  count.position.set(w / 2 - 18, 0);
  c.addChild(head, hex, glyph, name, count);
  c.position.set(cx, cy);
  return c;
}
